import React, { useCallback, useEffect, useState } from "react";
import "./ChatList.css";
import { getConversations } from "../../api/chatApi";

// Вспомогательная функция: "в сети вчера в 21:15" / "σήμερα" и т.п.
const formatLastSeen = (isoString) => {
  if (!isoString) return "";
  const dt = new Date(isoString);
  const now = new Date();

  const isToday =
    dt.toDateString() === now.toDateString();

  const yesterday = new Date();
  yesterday.setDate(now.getDate() - 1);
  const isYesterday =
    dt.toDateString() === yesterday.toDateString();

  const time = dt.toLocaleTimeString("el-GR", {
    hour: "2-digit",
    minute: "2-digit",
  });

  if (isToday) {
    return `σήμερα στις ${time}`;        // сегодня в 21:15
  }
  if (isYesterday) {
    return `χθες στις ${time}`;          // вчера в 21:15
  }
  return dt.toLocaleDateString("el-GR", {
    day: "2-digit",
    month: "2-digit",
  }) + `, ${time}`;
};

const getLastMessagePreview = (message) => {
  if (!message) {
    return "Δεν υπάρχουν ακόμη μηνύματα";
  }

  const text = message.text?.trim();

  if (text) {
    return text;
  }

  const attachments = Array.isArray(message.attachments)
    ? message.attachments.filter(Boolean)
    : [];

  if (!attachments.length) {
    return "Δεν υπάρχουν ακόμη μηνύματα";
  }

  const readyAttachments = attachments.filter(
    (attachment) => attachment.status === "ready"
  );

  const visibleAttachments = readyAttachments.length
    ? readyAttachments
    : attachments;

  if (visibleAttachments.length > 1) {
    const allImages = visibleAttachments.every(
      (attachment) => attachment.attachment_type === "image"
    );

    const allVideos = visibleAttachments.every(
      (attachment) => attachment.attachment_type === "video"
    );

    if (allImages) {
      return `📷 ${visibleAttachments.length} φωτογραφίες`;
    }

    if (allVideos) {
      return `🎬 ${visibleAttachments.length} βίντεο`;
    }

    return `📎 ${visibleAttachments.length} αρχεία`;
  }

  const attachment = visibleAttachments[0];

  if (attachment.status !== "ready") {
    return "📎 Αρχείο";
  }

  if (attachment.attachment_type === "image") {
    return "📷 Φωτογραφία";
  }

  if (attachment.attachment_type === "video") {
    return "🎬 Βίντεο";
  }

  return `📎 ${attachment.original_name || "Αρχείο"}`;
};

const ChatList = ({
  token,
  currentProfileId,
  autoOpenId,
  readConversationIds = [],
  onSelectConversation,
}) => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);  

  const loadConversations = useCallback(async ({ silent = false } = {}) => {
    if (!token) {
      setConversations([]);
      setLoading(false);
      return;
    }

    try {
      if (!silent) {
        setLoading(true);
      }

      setError(null);

      const data = await getConversations(token);
      setConversations(data);
    } catch (err) {
      console.error("Ошибка при загрузке чатов:", err);
      setError("Αποτυχία φόρτωσης συνομιλιών"); // "Ошибка загрузки чатов"
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [token]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  useEffect(() => {
    const handleRefreshChatList = () => {
      loadConversations({ silent: true });
    };

    window.addEventListener("refresh-chat-list", handleRefreshChatList);

    return () => {
      window.removeEventListener("refresh-chat-list", handleRefreshChatList);
    };
  }, [loadConversations]);

  useEffect(() => {
  const handlePresenceUpdated = (event) => {
    const data = event.detail;

    if (!data?.profile_id) {
      return;
    }

    setConversations((prev) => {
      const profileId = String(data.profile_id);

      return prev.map((conv) => {
        let changed = false;

        const updateProfile = (profile) => {
          if (!profile || String(profile.id) !== profileId) {
            return profile;
          }

          changed = true;

          return {
            ...profile,
            is_online: Boolean(data.is_online),
            last_seen: data.last_seen ?? profile.last_seen,
          };
        };

        const nextA = updateProfile(conv.a);
        const nextB = updateProfile(conv.b);

        if (!changed) {
          return conv;
        }

        return {
          ...conv,
          a: nextA,
          b: nextB,
        };
      });
    });
  };

  window.addEventListener("profile-presence-updated", handlePresenceUpdated);

  return () => {
    window.removeEventListener("profile-presence-updated", handlePresenceUpdated);
  };
}, []);
    

    useEffect(() => {
    if (!autoOpenId) return;

    const autoOpenKey = String(autoOpenId);
    const hit = conversations.find(
      (c) => String(c.id) === autoOpenKey
    );

    if (hit) {
      onSelectConversation(hit);
    }
  }, [autoOpenId, conversations, onSelectConversation]);

  if (loading) return <div className="chatlist-loading">Φόρτωση...</div>;
  if (error) return <div className="chatlist-error">{error}</div>;
  if (!conversations.length)
    return <div className="chatlist-empty">Δεν υπάρχουν συνομιλίες</div>;


  return (
    <div className="chatlist-container">
      {conversations.map((conv) => {
        const meId = currentProfileId;
        const isReadLocally = readConversationIds.includes(conv.id);
        const hasUnread = conv.has_unread && !isReadLocally;

        // кто собеседник
        const partner =
          meId && conv.a.id === meId
            ? conv.b
            : meId && conv.b.id === meId
            ? conv.a
            : conv.b;

        // заголовок + цена объявления
        const adTitle = conv.ad_title || "Χωρίς τίτλο";
        const price = conv.ad_price;
        const adLine = price ? `${adTitle} · ${price} €` : adTitle;

        // превью последнего сообщения
        const lastMessageText = getLastMessagePreview(conv.last_message);

        // «был в сети ...»
        const lastSeenLabel = partner.is_online
          ? "Ενεργώς"
          : formatLastSeen(partner.last_seen);

        return (
          <div
            key={conv.id}
            className={`chatlist-item ${hasUnread ? "is-unread" : "is-read"}`}
            onClick={() => onSelectConversation(conv)}
          >
            {/* слева — превью объявления + маленький аватар */}
            <div className="chatlist-thumb-wrapper">
              {conv.ad_image ? (
                <img
                  src={conv.ad_image}
                  alt={adTitle}
                  className="chatlist-ad-image"
                />
              ) : (
                <div className="chatlist-ad-placeholder">χωρίς φωτο</div>
              )}

              {partner.avatar && (
                <img
                  src={partner.avatar}
                  alt={partner.name}
                  className="chatlist-avatar-badge"
                />
              )}
            </div>

            {/* по центру — ФИО + строка "объявление · цена" */}
            <div className="chatlist-info">
              <div className="chatlist-name-row">
                <div className="chatlist-name">
                  {partner.name || "Χωρίς όνομα"}
                </div>

                {hasUnread && <span className="chatlist-unread-dot" />}
              </div>
              <div className="chatlist-last">
                {adLine}
              </div>
              <div className="chatlist-preview">
                {lastMessageText}
              </div>
            </div>

            {/* справа — когда был в сети */}
            <div className={`chatlist-time ${partner.is_online ? "is-online" : ""}`}>
              {lastSeenLabel}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default ChatList;